import { useCallback, useRef } from 'react'

import { useQuery, useQueryClient } from '@tanstack/react-query'

import { QUERY_KEYS } from '~/constants/react-query'
import { useActiveOrgId } from '~/hooks/org'
import { useServerRequests } from '~/hooks/useRequests'
import { PostOnboardingChecklist } from '~/types'

type ChecklistResponse = {
  // Null for orgs that completed onboarding before the checklist shipped.
  checklist: PostOnboardingChecklist | null
}

export const getPostOnboardingChecklistQueryKey = (
  activeOrgId: string | undefined,
) => [QUERY_KEYS.POST_ONBOARDING_CHECKLIST, activeOrgId]

// Poll while the checklist has an open step. The backend completes every step
// out of band: a transfer indexes, an invitee accepts, the earn module syncs.
// Nothing else tells an open tab that it happened.
const CHECKLIST_REFETCH_INTERVAL = 10_000
// An org that never finishes would otherwise poll for as long as a tab stays
// open. Past this, the next load picks the change up.
const CHECKLIST_POLL_WINDOW = 30 * 60 * 1000

const getRefetchInterval = ({
  checklist,
  pollingSince,
}: {
  checklist: PostOnboardingChecklist | null | undefined
  pollingSince: number
}) => {
  // Hidden is per-user: this user sees no checklist surface, so there is
  // nothing for a fresh FUND step to update.
  if (!checklist || checklist.completedAt || checklist.hidden) return false
  if (Date.now() - pollingSince > CHECKLIST_POLL_WINDOW) return false
  return CHECKLIST_REFETCH_INTERVAL
}

export const usePostOnboardingChecklist = (): {
  checklist: PostOnboardingChecklist | null
  isLoading: boolean
} => {
  const { serverGetRequest } = useServerRequests()
  const activeOrgId = useActiveOrgId()
  // Stamped on the first interval check rather than during render, where
  // Date.now() is an impure call.
  const pollingSinceRef = useRef<number | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: getPostOnboardingChecklistQueryKey(activeOrgId),
    queryFn: async (): Promise<ChecklistResponse> => {
      if (!activeOrgId) throw new Error('No orgId')

      const response = await serverGetRequest<ChecklistResponse>({
        path: '/api/org/post_onboarding_checklist',
        params: { orgId: activeOrgId },
      })
      if (!response) {
        throw new Error('Failed to fetch the post-onboarding checklist')
      }

      return response.data
    },
    enabled: !!activeOrgId,
    refetchInterval: (query) => {
      pollingSinceRef.current ??= Date.now()
      return getRefetchInterval({
        checklist: query.state.data?.checklist,
        pollingSince: pollingSinceRef.current,
      })
    },
  })

  return { checklist: data?.checklist ?? null, isLoading }
}

export const usePostOnboardingChecklistUpdates = () => {
  const queryClient = useQueryClient()
  const activeOrgId = useActiveOrgId()

  const setPostOnboardingChecklist = useCallback(
    async (checklist: PostOnboardingChecklist | null) => {
      const queryKey = getPostOnboardingChecklistQueryKey(activeOrgId)
      // A poll response in flight would otherwise overwrite the hide.
      await queryClient.cancelQueries({ queryKey })
      queryClient.setQueryData<ChecklistResponse>(queryKey, { checklist })
    },
    [queryClient, activeOrgId],
  )

  return { setPostOnboardingChecklist }
}
